/**
 * Custom canvas-renderer shapes for insulation call-outs: cross-hatch/hachure
 * fills via rough.js for polygon/circle/triangle symbols, and coil/squiggle/
 * rect-wave line renderers for insulated pipe/duct segments.
 *
 * Split out of styleBuilder.ts so the hand-rolled drawing algorithms live
 * apart from the declarative Fill/Stroke/Text style builders. @2026/09/09
 */

import { Style } from 'ol/style';
import rough from 'roughjs';

// rough.js generator instance (module-level, created once)
const _roughGen = rough.generator();

/**
 * Polygon style with a cross-hatch fill drawn via rough.js,
 * used to visually indicate that a symbol has insulation attached.
 *
 * @param strokeColor border + hatch colour (matches version palette)
 * @param strokeWidth border line width
 * @param opacity overall style opacity, lighter for the selected-state overlay
 * @returns OpenLayers Style with a custom canvas renderer
 */
export const buildInsulatedPolygonStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  opacity = 1,
): Style => {
  return new Style({
    // Keep the modifyGeometry pattern so resizing works correctly (TAK-501)
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    renderer: function (coordinates, state) {
      const ctx = state.context as CanvasRenderingContext2D;
      // For Polygon geometry coordinates is Coordinate[][] (outer ring first)
      const ring = (coordinates as number[][][])[0];
      // rough.js polygon: exclude the duplicate closing vertex
      const points = ring.slice(0, -1) as [number, number][];

      ctx.save();

      // 1. Hachure fill via rough.js generator (no DOM canvas needed)
      // NOTE: cross-hatch lines are STROKED paths (p.stroke), not filled (p.fill)
      const drawable = _roughGen.polygon(points, {
        stroke: 'none',
        fill: strokeColor,
        fillStyle: 'hachure', // 'cross-hatch' previously - @2026/09/09
        hachureAngle: 45,
        hachureGap: 12,
        fillWeight: 2,
        roughness: 0,
      });
      const paths = _roughGen.toPaths(drawable);
      ctx.globalAlpha = 0.45 * opacity;
      for (const p of paths) {
        if (!p.d) continue;
        const path2d = new Path2D(p.d);
        if (p.stroke && p.stroke !== 'none') {
          ctx.strokeStyle = p.stroke;
          ctx.lineWidth = p.strokeWidth ?? 1;
          ctx.stroke(path2d);
        }
        if (p.fill && p.fill !== 'none') {
          ctx.fillStyle = p.fill;
          ctx.fill(path2d);
        }
      }
      ctx.globalAlpha = opacity;

      // 2. Draw the polygon border on top
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      const borderPath = new Path2D();
      borderPath.moveTo(ring[0][0], ring[0][1]);
      for (let i = 1; i < ring.length; i++) {
        borderPath.lineTo(ring[i][0], ring[i][1]);
      }
      borderPath.closePath();
      ctx.stroke(borderPath);

      ctx.restore();
    },
  });
};

export const buildInsulatedCircleStyle = (
  color: string,
  strokeWidth: number,
): Style => {
  return new Style({
    renderer: (coordinates, state) => {
      const ctx = state.context as CanvasRenderingContext2D;
      const coords = coordinates as number[][];
      const [cx, cy] = coords[0];
      const [ex, ey] = coords[1];
      const pixelRadius = Math.sqrt((ex - cx) ** 2 + (ey - cy) ** 2);

      ctx.save();

      const drawable = _roughGen.circle(cx, cy, pixelRadius * 2, {
        stroke: 'none',
        fill: color,
        fillStyle: 'cross-hatch',
        hachureAngle: 45,
        hachureGap: 10,
        fillWeight: 2,
        roughness: 0,
      });
      ctx.globalAlpha = 0.45;
      for (const p of _roughGen.toPaths(drawable)) {
        if (!p.d) continue;
        const path2d = new Path2D(p.d);
        if (p.stroke && p.stroke !== 'none') {
          ctx.strokeStyle = p.stroke;
          ctx.lineWidth = p.strokeWidth ?? 1;
          ctx.stroke(path2d);
        }
      }
      ctx.globalAlpha = 1.0;

      // Border on top
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.beginPath();
      ctx.arc(cx, cy, pixelRadius, 0, 2 * Math.PI);
      ctx.stroke();

      ctx.restore();
    },
  });
};

export const buildInsulatedTriangleStyle = (
  color: string,
  radius: number,
  strokeWidth: number,
): Style => {
  const rotation = Math.PI / 4; // must match basicTriangleStyle
  return new Style({
    renderer: (coordinates, state) => {
      const ctx = state.context as CanvasRenderingContext2D;
      const [cx, cy] = coordinates as number[];

      // Compute vertices using the same formula OpenLayers uses for RegularShape
      const verts: [number, number][] = Array.from({ length: 3 }, (_, i) => {
        const a = rotation + i * ((2 * Math.PI) / 3);
        return [cx + radius * Math.sin(a), cy - radius * Math.cos(a)];
      });

      ctx.save();

      const drawable = _roughGen.polygon(verts, {
        stroke: 'none',
        fill: color,
        fillStyle: 'cross-hatch',
        hachureAngle: 45,
        hachureGap: 10,
        fillWeight: 2,
        roughness: 0,
      });
      ctx.globalAlpha = 0.45;
      for (const p of _roughGen.toPaths(drawable)) {
        if (!p.d) continue;
        const path2d = new Path2D(p.d);
        if (p.stroke && p.stroke !== 'none') {
          ctx.strokeStyle = p.stroke;
          ctx.lineWidth = p.strokeWidth ?? 1;
          ctx.stroke(path2d);
        }
      }
      ctx.globalAlpha = 1.0;

      // Border on top
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      const border = new Path2D();
      border.moveTo(verts[0][0], verts[0][1]);
      for (let i = 1; i < verts.length; i++) {
        border.lineTo(verts[i][0], verts[i][1]);
      }
      border.closePath();
      ctx.stroke(border);

      ctx.restore();
    },
  });
};

export const buildInsulatedLineStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  style: 'coil' | 'squiggle' | 'rect' = 'coil',
  opacity = 1,
): Style => {
  // Coil geometry constants — all sized relative to strokeWidth
  const coilRadiusPerp = strokeWidth * 4; // major axis: perpendicular to the pipe
  const coilRadiusAlong = strokeWidth * 1.5; // minor axis: along the pipe
  const coilSpacing = coilRadiusAlong * 4; // center-to-center distance between coils

  /** Draw one semicircular coil arc at position (cx, cy), rotated to `angle`. */
  const drawCoilArc = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    angle: number,
    startAngle: number,
    endAngle: number,
  ) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.ellipse(0, 0, coilRadiusAlong, coilRadiusPerp, 0, startAngle, endAngle);
    ctx.stroke();
    ctx.restore();
  };

  // Squiggle geometry constants
  const squiggleR = strokeWidth * 5; // arc radius (circular — same along and perpendicular)
  const squiggleStep = squiggleR * 2; // arc center-to-center = full arc width so arcs connect

  /**
   * Draw one semicircular squiggle arc centered at (cx, cy), rotated to `angle`.
   * side=1 bulges away from the line (negative y in rotated frame); side=-1 bulges toward.
   * Both arcs share the same lineWidth and globalAlpha — solid, uniform squiggle.
   */
  const drawSquiggleArc = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    angle: number,
    side: 1 | -1,
  ) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    if (side === 1) {
      ctx.ellipse(0, 0, squiggleR, squiggleR, 0, Math.PI, Math.PI * 2);
    } else {
      ctx.ellipse(0, 0, squiggleR, squiggleR, 0, 0, Math.PI);
    }
    ctx.stroke();
    ctx.restore();
  };

  const renderSquiggleSegment = (
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segLength = Math.sqrt(dx * dx + dy * dy);
    if (segLength < squiggleStep * 2) return;

    const angle = Math.atan2(dy, dx);
    const numArcs = Math.floor(segLength / squiggleStep);
    const startOffset = (segLength - numArcs * squiggleStep) / 2 + squiggleR;

    ctx.lineWidth = strokeWidth;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.lineWidth = strokeWidth * 1.5;
    for (let c = 0; c < numArcs; c++) {
      const t = startOffset + c * squiggleStep;
      drawSquiggleArc(
        ctx,
        x1 + (dx / segLength) * t,
        y1 + (dy / segLength) * t,
        angle,
        c % 2 === 0 ? 1 : -1,
      );
    }
  };

  const renderCoilSegment = (
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segLength = Math.sqrt(dx * dx + dy * dy);
    if (segLength < coilSpacing) return;

    const angle = Math.atan2(dy, dx);
    const numCoils = Math.floor(segLength / coilSpacing);
    const startOffset =
      (segLength - numCoils * coilSpacing) / 2 + coilSpacing / 2;
    const coilCenters = Array.from({ length: numCoils }, (_, c) => {
      const t = startOffset + c * coilSpacing;
      return [x1 + (dx / segLength) * t, y1 + (dy / segLength) * t];
    });

    ctx.globalAlpha = 0.3 * opacity;
    ctx.lineWidth = strokeWidth * 1.5;
    for (const [cx, cy] of coilCenters)
      drawCoilArc(ctx, cx, cy, angle, Math.PI, Math.PI * 2);

    ctx.globalAlpha = opacity;
    ctx.lineWidth = strokeWidth;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.lineWidth = strokeWidth * 2;
    for (const [cx, cy] of coilCenters)
      drawCoilArc(ctx, cx, cy, angle, 0, Math.PI);
  };

  // Rectangular ("square wave") geometry constants — bigger, sparser teeth
  // @2026/09/09
  const rectAmplitude = strokeWidth * 3; // swing from centerline to a tooth's outer edge
  const rectStep = strokeWidth * 6; // horizontal length of each tooth

  /**
   * Draw one continuous square-wave path along the segment: each tooth swings
   * from the centerline out to +amplitude, across, then straight through to
   * -amplitude, across, and so on — a symmetric crenellation like BS/ASME
   * insulation call-outs. Falls back to a plain line when too short for a
   * full tooth.
   */
  const renderRectSegment = (
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segLength = Math.sqrt(dx * dx + dy * dy);

    ctx.lineWidth = strokeWidth + 1;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    if (segLength < rectStep * 2) return;

    const ux = dx / segLength;
    const uy = dy / segLength;
    const nx = -uy; // unit vector perpendicular to the pipe
    const ny = ux;

    const numTeeth = Math.floor(segLength / rectStep);
    const startOffset = (segLength - numTeeth * rectStep) / 2;

    ctx.lineJoin = 'miter';
    ctx.lineWidth = strokeWidth + 2;
    ctx.beginPath();
    let curX = x1 + ux * startOffset;
    let curY = y1 + uy * startOffset;
    ctx.moveTo(curX, curY);
    for (let i = 0; i < numTeeth; i++) {
      const level = i % 2 === 0 ? 1 : -1;
      const nextX = curX + ux * rectStep;
      const nextY = curY + uy * rectStep;
      ctx.lineTo(
        curX + nx * rectAmplitude * level,
        curY + ny * rectAmplitude * level,
      );
      ctx.lineTo(
        nextX + nx * rectAmplitude * level,
        nextY + ny * rectAmplitude * level,
      );
      curX = nextX;
      curY = nextY;
    }
    ctx.lineTo(curX, curY);
    ctx.stroke();
  };

  const renderSegment =
    style === 'squiggle'
      ? renderSquiggleSegment
      : style === 'rect'
        ? renderRectSegment
        : renderCoilSegment;

  return new Style({
    renderer: function (coordinates, state) {
      const ctx = state.context as CanvasRenderingContext2D;
      const coords = coordinates as number[][];
      if (coords.length < 2) return;

      ctx.save();
      ctx.strokeStyle = strokeColor;
      ctx.globalAlpha = opacity;
      for (let seg = 0; seg < coords.length - 1; seg++) {
        const [x1, y1] = coords[seg];
        const [x2, y2] = coords[seg + 1];
        renderSegment(ctx, x1, y1, x2, y2);
      }
      ctx.restore();
    },
  });
};
